import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Paper,
  Stack,
  Typography,
  Button,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Menu,
  MenuItem,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SettingsIcon from '@mui/icons-material/Settings';
import TimelineIcon from '@mui/icons-material/Timeline';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import MoreHorizRoundedIcon from '@mui/icons-material/MoreHorizRounded';
import { useUnit } from 'effector-react';
import { buildingsApi } from '../../api/buildings';
import { heatApi } from '../../api/heat';
import { meterReadingsImportApi } from '../../api/meterReadingsImport';
import { devicesApi } from '../../api/devices';
import {
  buildReportPeriodWindow,
  formatPeriodRange,
  formatPeriodRuleLabel,
  localYearMonth,
} from '../../utils/dates';
import { downloadBlobAsFile } from '../../utils/download';
import ReplaceDeviceDialog from '../Devices/ReplaceDeviceDialog';
import { $tenantDataStartDate } from '../../store/auth';

const defaultSettings = {
  heat_constant_share_percent: '',
  heat_variable_share_percent: '',
  heat_default_tariff: '',
  heat_min_readings_percent: '',
  heat_period_start_day: '',
  heat_period_end_day: '',
};

const emptyPeriod = {
  report_month: '',
  period_start: '',
  period_end: '',
  house_consumption_gcal: '',
  tariff: '',
  constant_share_percent: '',
  variable_share_percent: '',
  ignore_reduction_coefficients: false,
  notes: '',
};

const emptyRadiator = {
  catalog_id: null,
  valid_from: '',
  reason: '',
  model_name: '',
  manufacturer: '',
  placement: '',
  room_label: '',
  section_count: '',
  power_watts: '',
  height_mm: '',
  width_mm: '',
  depth_mm: '',
  is_corner: false,
  heat_coefficient: '',
  heat_reduction_coefficient: '',
  notes: '',
};

const emptyCatalogForm = {
  id: null,
  name: '',
  manufacturer: '',
  model_code: '',
  radiator_type: '',
  placement: '',
  section_count: '',
  power_watts: '',
  height_mm: '',
  width_mm: '',
  depth_mm: '',
  is_corner: false,
  heat_coefficient: '',
  notes: '',
  is_active: true,
};

const isHeatAllocatorType = (value) => {
  if (!value) return false;
  const normalized = String(value).toLowerCase();
  return (
    normalized === 'heat_allocator' ||
    normalized === 'урт' ||
    normalized.includes('heat_allocator') ||
    normalized.includes('heat') ||
    normalized.includes('urt') ||
    normalized.includes('урт') ||
    normalized.includes('теплораспредел')
  );
};

const getCurrentReportMonth = () => localYearMonth(0);

const buildPeriodWindow = (reportMonth, startDay, endDay) =>
  buildReportPeriodWindow(reportMonth, startDay, endDay);

const sanitizeFilenamePart = (value, fallback = 'report') => {
  const text = String(value || '').trim() || fallback;
  return text
    .replace(/[\\/:*?"<>|]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 120) || fallback;
};

export default function HeatManagementPanel({ buildingId, defaultExpanded = false, onModeChange }) {
  const navigate = useNavigate();
  const tenantDataStartDate = useUnit($tenantDataStartDate);
  const [loading, setLoading] = useState(false);
  const [periodsLoading, setPeriodsLoading] = useState(false);
  const [heatSettings, setHeatSettings] = useState(defaultSettings);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [periods, setPeriods] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sessionActionId, setSessionActionId] = useState(null);
  const [periodDialog, setPeriodDialog] = useState({ open: false, editing: null, data: emptyPeriod });
  const [savingPeriod, setSavingPeriod] = useState(false);
  const [devicesQuery, setDevicesQuery] = useState('');
  const [devicesResult, setDevicesResult] = useState([]);
  const [searchingDevices, setSearchingDevices] = useState(false);
  const [radiatorCatalogItems, setRadiatorCatalogItems] = useState([]);
  const [catalogDialogOpen, setCatalogDialogOpen] = useState(false);
  const [catalogForm, setCatalogForm] = useState(emptyCatalogForm);
  const [catalogSaving, setCatalogSaving] = useState(false);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [radiatorDialog, setRadiatorDialog] = useState({
    open: false,
    device: null,
    loading: false,
    saving: false,
    replacing: false,
    revisions: [],
    data: emptyRadiator,
  });
  const [replaceDeviceDialog, setReplaceDeviceDialog] = useState({ open: false, device: null });
  const [reportDialog, setReportDialog] = useState({ open: false, loading: false, payload: null });
  const [reportDownloading, setReportDownloading] = useState(false);
  const [attachImportDialog, setAttachImportDialog] = useState({
    open: false,
    session: null,
    loading: false,
    items: [],
  });
  const [heatAllocatorEnabled, setHeatAllocatorEnabled] = useState(false);
  const [heatDataLoaded, setHeatDataLoaded] = useState(false);
  const [heatFlagSaving, setHeatFlagSaving] = useState(false);
  const [readiness, setReadiness] = useState(null);
  const [readinessLoading, setReadinessLoading] = useState(false);
  const [allowMissingCoefficientsArea, setAllowMissingCoefficientsArea] = useState(false);
  const [error, setError] = useState('');
  const [periodNotice, setPeriodNotice] = useState('');
  const [periodActionId, setPeriodActionId] = useState(null);
  const [expanded, setExpanded] = useState(defaultExpanded);

  const loadBuilding = useCallback(async () => {
    if (!buildingId) return;
    setLoading(true);
    setError('');
    try {
      const data = await buildingsApi.getBuildingById(String(buildingId));
      setHeatAllocatorEnabled(Boolean(data.heat_allocator_enabled));
      setHeatSettings({
        heat_constant_share_percent:
          data.heat_constant_share_percent !== null && data.heat_constant_share_percent !== undefined
            ? String(data.heat_constant_share_percent)
            : '',
        heat_variable_share_percent:
          data.heat_variable_share_percent !== null && data.heat_variable_share_percent !== undefined
            ? String(data.heat_variable_share_percent)
            : '',
        heat_default_tariff:
          data.heat_default_tariff !== null && data.heat_default_tariff !== undefined
            ? String(data.heat_default_tariff)
            : '',
        heat_min_readings_percent:
          data.heat_min_readings_percent !== null && data.heat_min_readings_percent !== undefined
            ? String(data.heat_min_readings_percent)
            : '',
        heat_period_start_day:
          data.heat_period_start_day !== null && data.heat_period_start_day !== undefined
            ? String(data.heat_period_start_day)
            : '',
        heat_period_end_day:
          data.heat_period_end_day !== null && data.heat_period_end_day !== undefined
            ? String(data.heat_period_end_day)
            : '',
      });
    } catch (err) {
      setError('Не удалось загрузить настройки теплоснабжения здания.');
    } finally {
      setLoading(false);
      setHeatDataLoaded(true);
    }
  }, [buildingId]);

  const loadPeriods = useCallback(async () => {
    if (!buildingId) return;
    setPeriodsLoading(true);
    try {
      const list = await heatApi.listPeriods(String(buildingId));
      setPeriods(list);
    } catch (err) {
      setError('Не удалось загрузить периоды расчёта тепла.');
    } finally {
      setPeriodsLoading(false);
    }
  }, [buildingId]);

  const loadSessions = useCallback(async () => {
    if (!buildingId) return;
    setSessionsLoading(true);
    try {
      const data = await heatApi.listSessions(String(buildingId));
      setSessions(data?.items || []);
    } catch (err) {
      setError('Не удалось загрузить сессии расчёта УРТ.');
    } finally {
      setSessionsLoading(false);
    }
  }, [buildingId]);

  const loadReadiness = useCallback(async (periodId = null) => {
    if (!buildingId) return;
    setReadinessLoading(true);
    try {
      const data = await heatApi.getReadiness(String(buildingId), periodId || undefined);
      setReadiness(data);
    } catch (err) {
      setReadiness(null);
      setError('Не удалось проверить готовность расчёта УРТ.');
    } finally {
      setReadinessLoading(false);
    }
  }, [buildingId]);

  const loadRadiatorCatalog = useCallback(async (query = '') => {
    setCatalogLoading(true);
    try {
      const data = await heatApi.listCatalog(query);
      setRadiatorCatalogItems(data.items || []);
      return data.items || [];
    } catch (err) {
      setRadiatorCatalogItems([]);
      setError('Не удалось загрузить справочник радиаторов УРТ.');
      return [];
    } finally {
      setCatalogLoading(false);
    }
  }, []);

  const handleOpenCatalogDialog = useCallback(async () => {
    await loadRadiatorCatalog();
    setCatalogDialogOpen(true);
  }, [loadRadiatorCatalog]);

  const handleEditCatalogItem = useCallback((item) => {
    setCatalogForm({
      id: item.id,
      name: item.name || '',
      manufacturer: item.manufacturer || '',
      model_code: item.model_code || '',
      radiator_type: item.radiator_type || '',
      placement: item.placement || '',
      section_count: item.section_count || '',
      power_watts: item.power_watts !== null && item.power_watts !== undefined ? String(item.power_watts) : '',
      height_mm: item.height_mm !== null && item.height_mm !== undefined ? String(item.height_mm) : '',
      width_mm: item.width_mm !== null && item.width_mm !== undefined ? String(item.width_mm) : '',
      depth_mm: item.depth_mm !== null && item.depth_mm !== undefined ? String(item.depth_mm) : '',
      is_corner: !!item.is_corner,
      heat_coefficient:
        item.heat_coefficient !== null && item.heat_coefficient !== undefined ? String(item.heat_coefficient) : '',
      notes: item.notes || '',
      is_active: item.is_active !== false,
    });
    setCatalogDialogOpen(true);
  }, []);

  const handleSaveCatalogItem = useCallback(async () => {
    if (!catalogForm.name.trim()) {
      setError('Для записи справочника укажите название модели радиатора.');
      return;
    }
    setCatalogSaving(true);
    const payload = {
      name: catalogForm.name.trim(),
      manufacturer: catalogForm.manufacturer || null,
      model_code: catalogForm.model_code || null,
      radiator_type: catalogForm.radiator_type || null,
      placement: catalogForm.placement || null,
      section_count: catalogForm.section_count || null,
      power_watts: catalogForm.power_watts === '' ? null : Number(catalogForm.power_watts),
      height_mm: catalogForm.height_mm === '' ? null : Number(catalogForm.height_mm),
      width_mm: catalogForm.width_mm === '' ? null : Number(catalogForm.width_mm),
      depth_mm: catalogForm.depth_mm === '' ? null : Number(catalogForm.depth_mm),
      is_corner: !!catalogForm.is_corner,
      heat_coefficient: catalogForm.heat_coefficient === '' ? null : Number(catalogForm.heat_coefficient),
      notes: catalogForm.notes || null,
      is_active: !!catalogForm.is_active,
    };
    try {
      if (catalogForm.id) {
        await heatApi.updateCatalogItem(catalogForm.id, payload);
      } else {
        await heatApi.createCatalogItem(payload);
      }
      await loadRadiatorCatalog();
      setCatalogForm(emptyCatalogForm);
      setCatalogDialogOpen(false);
    } catch (err) {
      setError('Не удалось сохранить запись справочника радиаторов.');
    } finally {
      setCatalogSaving(false);
    }
  }, [catalogForm, loadRadiatorCatalog]);

  const handleDeleteCatalogItem = useCallback(async (item) => {
    if (!window.confirm(`Удалить запись "${item.name}" из справочника радиаторов?`)) {
      return;
    }
    try {
      await heatApi.deleteCatalogItem(item.id);
      await loadRadiatorCatalog();
      if (catalogForm.id === item.id) {
        setCatalogForm(emptyCatalogForm);
      }
    } catch (err) {
      setError('Не удалось удалить запись справочника радиаторов.');
    }
  }, [catalogForm.id, loadRadiatorCatalog]);

  useEffect(() => {
    if (!buildingId) return;
    setHeatDataLoaded(false);
    loadBuilding();
    loadPeriods();
    loadSessions();
  }, [buildingId, loadBuilding, loadPeriods, loadSessions]);

  const handleSettingsSave = async () => {
    if (!buildingId) return;
    setSavingSettings(true);
    const constant = heatSettings.heat_constant_share_percent === '' ? null : Number(heatSettings.heat_constant_share_percent);
    const variable = heatSettings.heat_variable_share_percent === '' ? null : Number(heatSettings.heat_variable_share_percent);
    const tariff = heatSettings.heat_default_tariff === '' ? null : Number(heatSettings.heat_default_tariff);
    const minReadingsPercent =
      heatSettings.heat_min_readings_percent === '' ? null : Number(heatSettings.heat_min_readings_percent);
    const periodStartDay =
      heatSettings.heat_period_start_day === '' ? null : Number(heatSettings.heat_period_start_day);
    const periodEndDay =
      heatSettings.heat_period_end_day === '' ? null : Number(heatSettings.heat_period_end_day);
    if (
      constant !== null &&
      variable !== null &&
      (Number.isNaN(constant) || Number.isNaN(variable) || constant + variable !== 100)
    ) {
      setError('Доли постоянной и переменной части должны давать 100%.');
      setSavingSettings(false);
      return;
    }
    if (
      (periodStartDay !== null && (Number.isNaN(periodStartDay) || periodStartDay < 1 || periodStartDay > 31)) ||
      (periodEndDay !== null && (Number.isNaN(periodEndDay) || periodEndDay < 1 || periodEndDay > 31))
    ) {
      setError('Дни отчётного периода должны быть от 1 до 31.');
      setSavingSettings(false);
      return;
    }
    if ((periodStartDay === null) !== (periodEndDay === null)) {
      setError('Укажите оба дня периода или очистите оба.');
      setSavingSettings(false);
      return;
    }
    try {
      await buildingsApi.updateBuilding(String(buildingId), {
        heat_constant_share_percent: constant,
        heat_variable_share_percent: variable,
        heat_default_tariff: tariff,
        heat_min_readings_percent: minReadingsPercent,
        heat_period_start_day: periodStartDay,
        heat_period_end_day: periodEndDay,
      });
      setSettingsDialogOpen(false);
      await loadBuilding();
    } catch (err) {
      setError('Не удалось сохранить тепловые настройки здания.');
    } finally {
      setSavingSettings(false);
    }
  };

  const openPeriodDialog = (period = null) => {
    if (period) {
      setPeriodDialog({
        open: true,
        editing: period,
        data: {
          period_start: period.period_start,
          period_end: period.period_end,
          house_consumption_gcal: String(period.house_consumption_gcal),
          tariff: period.tariff !== null && period.tariff !== undefined ? String(period.tariff) : '',
          constant_share_percent:
            period.constant_share_percent !== null && period.constant_share_percent !== undefined
              ? String(period.constant_share_percent)
              : '',
          variable_share_percent:
            period.variable_share_percent !== null && period.variable_share_percent !== undefined
              ? String(period.variable_share_percent)
              : '',
          ignore_reduction_coefficients: !!period.ignore_reduction_coefficients,
          notes: period.notes || '',
        },
      });
    } else {
      const reportMonth = getCurrentReportMonth();
      const periodWindow = buildPeriodWindow(
        reportMonth,
        heatSettings.heat_period_start_day,
        heatSettings.heat_period_end_day
      );
      setPeriodDialog({
        open: true,
        editing: null,
        data: {
          ...emptyPeriod,
          report_month: reportMonth,
          period_start: periodWindow.period_start,
          period_end: periodWindow.period_end,
          tariff: heatSettings.heat_default_tariff ?? '',
          constant_share_percent: heatSettings.heat_constant_share_percent ?? '',
          variable_share_percent: heatSettings.heat_variable_share_percent ?? '',
          ignore_reduction_coefficients: false,
        },
      });
    }
  };

  const handleSavePeriod = async () => {
    if (!buildingId) return;
    setSavingPeriod(true);
    const payload = {
      period_start: periodDialog.data.period_start,
      period_end: periodDialog.data.period_end,
      house_consumption_gcal: Number(periodDialog.data.house_consumption_gcal || 0),
      tariff:
        periodDialog.data.tariff === '' || periodDialog.data.tariff === null
          ? null
          : Number(periodDialog.data.tariff),
      constant_share_percent:
        periodDialog.data.constant_share_percent === '' || periodDialog.data.constant_share_percent === null
          ? null
          : Number(periodDialog.data.constant_share_percent),
      variable_share_percent:
        periodDialog.data.variable_share_percent === '' || periodDialog.data.variable_share_percent === null
          ? null
          : Number(periodDialog.data.variable_share_percent),
      ignore_reduction_coefficients: !!periodDialog.data.ignore_reduction_coefficients,
      notes: periodDialog.data.notes || null,
    };
    try {
      if (periodDialog.editing) {
        await heatApi.updatePeriod(periodDialog.editing.id, payload);
      } else {
        await heatApi.createPeriod(String(buildingId), payload);
      }
      setPeriodDialog({ open: false, editing: null, data: emptyPeriod });
      await loadPeriods();
    } catch (err) {
      setError('Не удалось сохранить период расчёта тепла.');
    } finally {
      setSavingPeriod(false);
    }
  };

  const handleDeletePeriod = async (periodId) => {
    if (!window.confirm('Удалить выбранный период?')) return;
    try {
      await heatApi.deletePeriod(periodId);
      await loadPeriods();
    } catch (err) {
      setError('Не удалось удалить период.');
    }
  };

  const handleCopyPreviousReadings = async (period) => {
    if (!window.confirm('Заполнить только отсутствующие показания значениями предыдущего периода?')) return;
    try {
      setPeriodActionId(`copy:${period.id}`);
      setPeriodNotice('');
      const result = await heatApi.copyPreviousReadings(period.id);
      setPeriodNotice(result.message);
      await Promise.all([loadPeriods(), loadReadiness(period.id)]);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Не удалось дозаполнить показания.');
    } finally {
      setPeriodActionId(null);
    }
  };

  const handleDeletePeriodReadings = async (period) => {
    if (!window.confirm('Удалить импортированные и скопированные показания этого периода? Автоматические показания затронуты не будут.')) return;
    try {
      setPeriodActionId(`clear:${period.id}`);
      setPeriodNotice('');
      const result = await heatApi.deletePeriodReadings(period.id);
      setPeriodNotice(result.message);
      await Promise.all([loadPeriods(), loadReadiness(period.id)]);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Не удалось удалить показания периода.');
    } finally {
      setPeriodActionId(null);
    }
  };

  const handleTogglePeriodLock = async (period) => {
    try {
      setPeriodActionId(`lock:${period.id}`);
      setPeriodNotice('');
      await heatApi.setPeriodLock(period.id, !period.is_locked);
      setPeriodNotice(period.is_locked ? 'Период разблокирован.' : 'Период заблокирован.');
      await loadPeriods();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Не удалось изменить блокировку периода.');
    } finally {
      setPeriodActionId(null);
    }
  };

  const handleCreateSession = async (period) => {
    if (!buildingId || !period?.id) return;
    try {
      setSessionActionId(`create:${period.id}`);
      await heatApi.createSession(String(buildingId), {
        heat_period_id: period.id,
        title: `Сессия ${formatPeriodRange(period.period_start, period.period_end, { dateOnly: true })}`,
        source: 'FILE_UPLOAD',
      });
      await loadSessions();
    } catch (err) {
      setError('Не удалось создать сессию расчёта УРТ.');
    } finally {
      setSessionActionId(null);
    }
  };

  const handleCalculateSession = async (session) => {
    if (!session?.id) return;
    try {
      setSessionActionId(`calc:${session.id}`);
      const result = await heatApi.calculateSession(session.id, {
        allowMissingCoefficientsArea,
      });
      setReportDialog({ open: true, loading: false, payload: result.report });
      await loadSessions();
    } catch (err) {
      setError('Не удалось выполнить расчёт по сессии УРТ.');
    } finally {
      setSessionActionId(null);
    }
  };

  const handleCloseSession = async (session) => {
    if (!session?.id) return;
    try {
      setSessionActionId(`close:${session.id}`);
      await heatApi.closeSession(session.id);
      await Promise.all([loadSessions(), loadPeriods()]);
    } catch (err) {
      setError('Не удалось закрыть сессию УРТ.');
    } finally {
      setSessionActionId(null);
    }
  };

  const handleOpenAttachImportDialog = async (session) => {
    if (!session?.id) return;
    setAttachImportDialog({
      open: true,
      session,
      loading: true,
      items: [],
    });
    try {
      const data = await meterReadingsImportApi.listBatches({
        limit: 20,
        offset: 0,
        sort_by: 'created_at',
        sort_dir: 'desc',
      });
      setAttachImportDialog({
        open: true,
        session,
        loading: false,
        items: (data?.items || []).filter((item) => !item.rolled_back_at),
      });
    } catch (err) {
      setAttachImportDialog({
        open: true,
        session,
        loading: false,
        items: [],
      });
      setError('Не удалось загрузить список импортов показаний.');
    }
  };

  const handleAttachImport = async (batch) => {
    const session = attachImportDialog.session;
    if (!session?.id || !batch?.id) return;
    try {
      setSessionActionId(`attach:${session.id}`);
      await heatApi.attachImport(session.id, { import_batch_id: batch.id });
      setAttachImportDialog({ open: false, session: null, loading: false, items: [] });
      await loadSessions();
    } catch (err) {
      setError('Не удалось привязать импорт к сессии УРТ.');
    } finally {
      setSessionActionId(null);
    }
  };

  const handleSearchDevices = async () => {
    if (!devicesQuery.trim()) return;
    setSearchingDevices(true);
    setDevicesResult([]);
    try {
      const res = await devicesApi.searchDevices(devicesQuery.trim(), { page_size: 50 });
      const allocators = res.items.filter((item) => isHeatAllocatorType(item.system_type));
      setDevicesResult(allocators);
    } catch (err) {
      setError('Не удалось выполнить поиск устройств.');
    } finally {
      setSearchingDevices(false);
    }
  };

  const openRadiatorDialog = useCallback(async (device) => {
    setRadiatorDialog((prev) => ({
      ...prev,
      open: true,
      device,
      loading: true,
      revisions: [],
      data: emptyRadiator,
    }));
    try {
      const [catalogResult, radiatorResult, revisionsResult] = await Promise.allSettled([
        loadRadiatorCatalog(),
        heatApi.getRadiator(device.id),
        heatApi.listRadiatorRevisions(device.id),
      ]);
      if (catalogResult.status === 'fulfilled') {
        setRadiatorCatalogItems(catalogResult.value || []);
      }
      if (radiatorResult.status !== 'fulfilled') {
        throw new Error('radiator_not_found');
      }
      const data = radiatorResult.value;
      setRadiatorDialog({
        open: true,
        device,
        loading: false,
        saving: false,
        replacing: false,
        revisions: revisionsResult.status === 'fulfilled' ? revisionsResult.value || [] : [],
        data: {
          catalog_id: data.catalog_id || null,
          valid_from: '',
          reason: '',
          model_name: data.model_name || '',
          manufacturer: data.manufacturer || '',
          placement: data.placement || '',
          room_label: data.room_label || '',
          section_count:
            data.section_count !== null && data.section_count !== undefined ? String(data.section_count) : '',
          power_watts:
            data.power_watts !== null && data.power_watts !== undefined ? String(data.power_watts) : '',
          height_mm: data.height_mm !== null && data.height_mm !== undefined ? String(data.height_mm) : '',
          width_mm: data.width_mm !== null && data.width_mm !== undefined ? String(data.width_mm) : '',
          depth_mm: data.depth_mm !== null && data.depth_mm !== undefined ? String(data.depth_mm) : '',
          is_corner: !!data.is_corner,
          heat_coefficient:
            data.heat_coefficient !== null && data.heat_coefficient !== undefined
              ? String(data.heat_coefficient)
              : '',
          heat_reduction_coefficient:
            data.heat_reduction_coefficient !== null && data.heat_reduction_coefficient !== undefined
              ? String(data.heat_reduction_coefficient)
              : '',
          notes: data.notes || '',
        },
      });
    } catch (err) {
      try {
        const catalog = await loadRadiatorCatalog();
        setRadiatorCatalogItems(catalog || []);
      } catch (_catalogErr) {
        setRadiatorCatalogItems([]);
      }
      setRadiatorDialog({
        open: true,
        device,
        loading: false,
        saving: false,
        replacing: false,
        revisions: [],
        data: {
          ...emptyRadiator,
          heat_coefficient:
            device?.heat_coefficient !== null && device?.heat_coefficient !== undefined
              ? String(device.heat_coefficient)
              : '',
          heat_reduction_coefficient:
            device?.heat_reduction_coefficient !== null && device?.heat_reduction_coefficient !== undefined
              ? String(device.heat_reduction_coefficient)
              : '',
        },
      });
    }
  }, [loadRadiatorCatalog]);

  const handleSaveRadiator = async () => {
    if (!radiatorDialog.device) return;
    setRadiatorDialog((prev) => ({ ...prev, saving: true }));
    const data = radiatorDialog.data;
    const devicePatch = {
      heat_coefficient: data.heat_coefficient === '' ? null : Number(data.heat_coefficient),
      heat_reduction_coefficient:
        data.heat_reduction_coefficient === '' ? null : Number(data.heat_reduction_coefficient),
    };
    const payload = {
      catalog_id: data.catalog_id || null,
      model_name: data.model_name || null,
      manufacturer: data.manufacturer || null,
      placement: data.placement || null,
      room_label: data.room_label || null,
      section_count: data.section_count === '' ? null : Number(data.section_count),
      power_watts: data.power_watts === '' ? null : Number(data.power_watts),
      height_mm: data.height_mm === '' ? null : Number(data.height_mm),
      width_mm: data.width_mm === '' ? null : Number(data.width_mm),
      depth_mm: data.depth_mm === '' ? null : Number(data.depth_mm),
      is_corner: !!data.is_corner,
      notes: data.notes || null,
    };
    try {
      await devicesApi.updateDevice(radiatorDialog.device.id, devicePatch);
      await heatApi.upsertRadiator(radiatorDialog.device.id, payload);
      setRadiatorDialog({ open: false, device: null, loading: false, saving: false, data: emptyRadiator });
    } catch (err) {
      setError('Не удалось сохранить паспорт радиатора.');
      setRadiatorDialog((prev) => ({ ...prev, saving: false }));
    }
  };

  const handleReplaceRadiator = async () => {
    if (!radiatorDialog.device) return;
    const data = radiatorDialog.data;
    if (!data.valid_from) {
      setError('Укажите дату замены радиатора.');
      return;
    }
    setRadiatorDialog((prev) => ({ ...prev, replacing: true }));
    const devicePatch = {
      heat_coefficient: data.heat_coefficient === '' ? null : Number(data.heat_coefficient),
      heat_reduction_coefficient:
        data.heat_reduction_coefficient === '' ? null : Number(data.heat_reduction_coefficient),
    };
    const payload = {
      valid_from: data.valid_from,
      reason: data.reason || 'Замена радиатора',
      catalog_id: data.catalog_id || null,
      model_name: data.model_name || null,
      manufacturer: data.manufacturer || null,
      placement: data.placement || null,
      room_label: data.room_label || null,
      section_count: data.section_count === '' ? null : Number(data.section_count),
      power_watts: data.power_watts === '' ? null : Number(data.power_watts),
      height_mm: data.height_mm === '' ? null : Number(data.height_mm),
      width_mm: data.width_mm === '' ? null : Number(data.width_mm),
      depth_mm: data.depth_mm === '' ? null : Number(data.depth_mm),
      is_corner: !!data.is_corner,
      heat_coefficient: data.heat_coefficient === '' ? null : Number(data.heat_coefficient),
      heat_reduction_coefficient:
        data.heat_reduction_coefficient === '' ? null : Number(data.heat_reduction_coefficient),
      notes: data.notes || null,
    };
    try {
      await heatApi.replaceRadiator(radiatorDialog.device.id, payload);
      const revisions = await heatApi.listRadiatorRevisions(radiatorDialog.device.id);
      setRadiatorDialog((prev) => ({
        ...prev,
        replacing: false,
        revisions,
        data: { ...prev.data, valid_from: '', reason: '' },
      }));
    } catch (err) {
      setError('Не удалось зафиксировать замену радиатора.');
      setRadiatorDialog((prev) => ({ ...prev, replacing: false }));
    }
  };

  const handleToggleHeatAllocator = async (nextValue) => {
    if (!buildingId) return;
    setHeatFlagSaving(true);
    setError('');
    try {
      await buildingsApi.updateBuilding(String(buildingId), { heat_allocator_enabled: nextValue });
      setHeatAllocatorEnabled(nextValue);
      if (
        nextValue &&
        (!heatSettings.heat_period_start_day || !heatSettings.heat_period_end_day)
      ) {
        // Ask to configure per-building period rule when it is not set yet.
        setSettingsDialogOpen(true);
      }
      if (onModeChange) {
        onModeChange(nextValue);
      }
    } catch (err) {
      setError('Не удалось обновить статус работы по УРТ.');
    } finally {
      setHeatFlagSaving(false);
    }
  };

  const handleBuildReport = async (period) => {
    setReportDialog({ open: true, loading: true, payload: null });
    try {
      const payload = await heatApi.buildReport(period.id, {
        allowMissingCoefficientsArea,
      });
      setReportDialog({ open: true, loading: false, payload });
    } catch (err) {
      setReportDialog({ open: false, loading: false, payload: null });
      setError('Не удалось сформировать отчёт по теплу.');
    }
  };

  const handleOpenSessionReport = async (session) => {
    if (!session?.id) return;
    setReportDialog({ open: true, loading: true, payload: null });
    try {
      const payload = await heatApi.getSessionReport(session.id, {
        allowMissingCoefficientsArea,
      });
      setReportDialog({ open: true, loading: false, payload });
    } catch (err) {
      setReportDialog({ open: false, loading: false, payload: null });
      setError('Не удалось открыть сохранённый отчёт.');
    }
  };

  const handleDownloadReport = useCallback(async (format = 'full') => {
    if (!reportDialog.payload || reportDialog.loading) return;
    try {
      setReportDownloading(true);
      const blob = await heatApi.downloadReportExcel(String(reportDialog.payload.summary.period_id), format, {
        allowMissingCoefficientsArea,
      });
      const { summary } = reportDialog.payload;
      const reportMonth = String(summary.period_end || '').slice(0, 7);
      const buildingLabel = summary.building_address || summary.building_name || summary.building_id;
      const filename = `УРТ_${sanitizeFilenamePart(buildingLabel, 'дом')}_${reportMonth}_${sanitizeFilenamePart(format)}.xlsx`;
      downloadBlobAsFile(blob, filename);
    } catch (err) {
      setError('Не удалось выгрузить отчёт по теплу.');
    } finally {
      setReportDownloading(false);
    }
  }, [allowMissingCoefficientsArea, reportDialog.payload, reportDialog.loading]);

  const handleDownloadReceiptsZip = useCallback(async () => {
    if (!reportDialog.payload || reportDialog.loading) return;
    try {
      setReportDownloading(true);
      const { summary } = reportDialog.payload;
      const blob = await heatApi.downloadReceiptsZip(String(summary.period_id), {
        allowMissingCoefficientsArea,
      });
      const reportMonth = String(summary.period_end || '').slice(0, 7);
      const buildingLabel = summary.building_address || summary.building_name || summary.building_id;
      downloadBlobAsFile(
        blob,
        `billingservice_${sanitizeFilenamePart(buildingLabel, 'дом')}_${reportMonth}_квитанции.zip`
      );
    } catch (err) {
      setError('Не удалось скачать квитанции.');
    } finally {
      setReportDownloading(false);
    }
  }, [allowMissingCoefficientsArea, reportDialog.payload, reportDialog.loading]);

  const handleDownloadC300 = useCallback(async () => {
    if (!reportDialog.payload || reportDialog.loading) return;
    try {
      setReportDownloading(true);
      const { summary } = reportDialog.payload;
      const blob = await heatApi.downloadReportC300(String(summary.period_id), {
        allowMissingCoefficientsArea,
      });
      const reportMonth = String(summary.period_end || '').slice(0, 7);
      const buildingLabel = summary.building_address || summary.building_name || summary.building_id;
      downloadBlobAsFile(
        blob,
        `billingservice_УРТ_${sanitizeFilenamePart(buildingLabel, 'дом')}_${reportMonth}_С-300.csv`,
      );
    } catch (err) {
      setError('Не удалось скачать файл для С-300.');
    } finally {
      setReportDownloading(false);
    }
  }, [allowMissingCoefficientsArea, reportDialog.payload, reportDialog.loading]);

  const handleDownloadApartmentReceipt = useCallback(async (apartment) => {
    if (!reportDialog.payload || reportDialog.loading || !apartment?.apartment_id) return;
    try {
      setReportDownloading(true);
      const { summary } = reportDialog.payload;
      const blob = await heatApi.downloadApartmentReceipt(
        String(summary.period_id),
        String(apartment.apartment_id),
        'zip',
        { allowMissingCoefficientsArea },
      );
      const account = apartment.account_number || apartment.apartment_number || apartment.apartment_id;
      const buildingLabel = summary.building_address || summary.building_name || summary.building_id;
      downloadBlobAsFile(
        blob,
        `billingservice_${sanitizeFilenamePart(buildingLabel, 'дом')}_${sanitizeFilenamePart(apartment.apartment_number || 'кв')}_${sanitizeFilenamePart(account)}.zip`
      );
    } catch (err) {
      setError('Не удалось выгрузить квитанцию по квартире.');
    } finally {
      setReportDownloading(false);
    }
  }, [allowMissingCoefficientsArea, reportDialog.payload, reportDialog.loading]);

  const latestPeriod = useMemo(() => {
    if (!periods.length) return null;
    return periods
      .slice()
      .sort((a, b) => new Date(b.period_end).getTime() - new Date(a.period_end).getTime())[0];
  }, [periods]);

  useEffect(() => {
    if (!buildingId || !heatDataLoaded || !heatAllocatorEnabled) {
      setReadiness(null);
      return;
    }
    loadReadiness(latestPeriod?.id || null);
  }, [buildingId, heatAllocatorEnabled, heatDataLoaded, latestPeriod?.id, loadReadiness]);

  const summaryChip = latestPeriod
    ? `Последний период: ${formatPeriodRange(latestPeriod.period_start, latestPeriod.period_end, { dateOnly: true })}`
    : 'Периоды расчёта пока не заданы';
  const periodRuleChip = formatPeriodRuleLabel(
    heatSettings.heat_period_start_day,
    heatSettings.heat_period_end_day
  );

  
  if (!heatDataLoaded) {
    return (
      <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Загрузка данных по режиму УРТ...
        </Typography>
      </Paper>
    );
  }

  if (!heatAllocatorEnabled) {
    return (
      <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
        <Stack spacing={2}>
          <Typography variant="h6">УРТ для этого дома отключены</Typography>
          <Typography variant="body2" color="text.secondary">
            Включите режим УРТ, чтобы управлять периодами, коэффициентами и отчётами.
          </Typography>
          {error ? (
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          ) : null}
          <Button
            variant="contained"
            onClick={() => handleToggleHeatAllocator(true)}
            disabled={heatFlagSaving || loading}
          >
            Включить режим УРТ
          </Button>
        </Stack>
      </Paper>
    );
  }

  return (
    <>
    <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
      <Stack spacing={2}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
          <div>
            <Typography variant="h6">Ручное распределение тепла (УРТ)</Typography>
            <Typography variant="body2" color="text.secondary">
              Константа: {heatSettings.heat_constant_share_percent || '—'}% • Переменная:{' '}
              {heatSettings.heat_variable_share_percent || '—'}% • Тариф:{' '}
              {heatSettings.heat_default_tariff || '—'} ₽/Гкал
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {summaryChip} • {periodRuleChip}
            </Typography>
          </div>
          <Stack direction="row" spacing={1}>
            <Tooltip title="Перезагрузить данные">
              <span>
                <IconButton onClick={() => { loadBuilding(); loadPeriods(); loadSessions(); loadReadiness(latestPeriod?.id || null); }} disabled={loading || periodsLoading || sessionsLoading || readinessLoading}>
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Настроить параметры распределения">
              <span>
                <IconButton color="primary" onClick={() => setSettingsDialogOpen(true)} disabled={loading}>
                  <SettingsIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Button size="small" variant="outlined" onClick={handleOpenCatalogDialog} disabled={catalogLoading}>
              Подбор
            </Button>
            <FormControlLabel
              control={
                <Switch
                  checked={heatAllocatorEnabled}
                  onChange={(event) => handleToggleHeatAllocator(event.target.checked)}
                  disabled={heatFlagSaving}
                />
              }
              label="URT enabled"
            />
            <Button
              size="small"
              variant={expanded ? 'outlined' : 'contained'}
              onClick={() => setExpanded((prev) => !prev)}
            >
              {expanded ? 'Скрыть блок' : 'Показать блок'}
            </Button>
          </Stack>
        </Stack>

        {!expanded && error ? (
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        ) : null}

        {expanded && (
          <>
            {error ? (
              <Typography color="error" variant="body2">
                {error}
              </Typography>
            ) : null}
            {periodNotice ? <Alert severity="success" onClose={() => setPeriodNotice('')}>{periodNotice}</Alert> : null}

            <Paper
              variant="outlined"
              sx={{
                p: 1.25,
                borderRadius: 2,
                bgcolor: allowMissingCoefficientsArea ? 'rgba(22, 163, 74, 0.06)' : 'background.paper',
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={allowMissingCoefficientsArea}
                    onChange={(event) => setAllowMissingCoefficientsArea(event.target.checked)}
                  />
                }
                label="Квартиры с УРТ без расчётного коэффициента считать по площади"
              />
              <Typography variant="caption" color="text.secondary" display="block">
                При включении отчёт не блокируется из-за отсутствующего коэффициента УРТ: такие квартиры попадут в расчёт по площади.
              </Typography>
            </Paper>

            <HeatReadinessCard
              readiness={readiness}
              loading={readinessLoading}
              allowMissingCoefficientsArea={allowMissingCoefficientsArea}
            />

            <Divider />

            <Typography variant="subtitle1">Паспорта радиаторов</Typography>
            <Stack direction="row" spacing={2} flexWrap="wrap" alignItems="center">
              <TextField
                label="Поиск устройств (серийный номер, адрес...)"
                value={devicesQuery}
                onChange={(e) => setDevicesQuery(e.target.value)}
                sx={{ minWidth: 280 }}
              />
              <Button variant="outlined" onClick={handleSearchDevices} disabled={searchingDevices}>
                Найти устройства
              </Button>
              <Typography variant="body2" color="text.secondary">
                Найдены: {devicesResult.length}
              </Typography>
            </Stack>
            <DeviceResults
              results={devicesResult}
              loading={searchingDevices}
              onEdit={openRadiatorDialog}
              onReplace={(device) => setReplaceDeviceDialog({ open: true, device })}
              onOpenApartment={(device) => {
                if (device.apartment_id) {
                  navigate(`/apartments/${device.apartment_id}?deviceId=${device.id}`);
                }
              }}
            />

            <Divider sx={{ my: 1 }} />

            <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
              <Typography variant="subtitle1">Периоды расчёта тепла</Typography>
              <Button variant="contained" onClick={() => openPeriodDialog()}>
                Добавить период
              </Button>
            </Stack>
            <PeriodsList
              periods={periods}
              loading={periodsLoading}
              onEdit={openPeriodDialog}
              onDelete={handleDeletePeriod}
              onReport={handleBuildReport}
              onCreateSession={handleCreateSession}
              sessionActionId={sessionActionId}
              periodActionId={periodActionId}
              onCopyPrevious={handleCopyPreviousReadings}
              onClearReadings={handleDeletePeriodReadings}
              onToggleLock={handleTogglePeriodLock}
            />

            <Divider sx={{ my: 1 }} />

            <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
              <Typography variant="subtitle1">Сессии расчёта УРТ</Typography>
              <Typography variant="body2" color="text.secondary">
                Импорт, расчёт и закрытие периода в одном потоке
              </Typography>
            </Stack>
            <SessionsList
              sessions={sessions}
              loading={sessionsLoading}
              periods={periods}
              sessionActionId={sessionActionId}
              onAttachImport={handleOpenAttachImportDialog}
              onCalculate={handleCalculateSession}
              onClose={handleCloseSession}
              onOpenReport={handleOpenSessionReport}
            />
          </>
        )}
      </Stack>
    </Paper>
      <RadiatorCatalogDialog
        open={catalogDialogOpen}
        items={radiatorCatalogItems}
        form={catalogForm}
        setForm={setCatalogForm}
        loading={catalogLoading}
        saving={catalogSaving}
        onClose={() => {
          setCatalogDialogOpen(false);
          setCatalogForm(emptyCatalogForm);
        }}
        onSave={handleSaveCatalogItem}
        onEdit={handleEditCatalogItem}
        onDelete={handleDeleteCatalogItem}
      />
      <HeatSettingsDialog
        open={settingsDialogOpen}
        onClose={() => setSettingsDialogOpen(false)}
        settings={heatSettings}
        setSettings={setHeatSettings}
        loading={savingSettings}
        onSave={handleSettingsSave}
      />

      <PeriodDialog
        open={periodDialog.open}
        data={periodDialog.data}
        setData={(data) => setPeriodDialog((prev) => ({ ...prev, data }))}
        loading={savingPeriod}
        onClose={() => setPeriodDialog({ open: false, editing: null, data: emptyPeriod })}
        onSave={handleSavePeriod}
        isEdit={Boolean(periodDialog.editing)}
        periodStartDay={heatSettings.heat_period_start_day}
        periodEndDay={heatSettings.heat_period_end_day}
        minDate={tenantDataStartDate}
      />

      <RadiatorDialog
        open={radiatorDialog.open}
        data={radiatorDialog.data}
        setData={(data) => setRadiatorDialog((prev) => ({ ...prev, data }))}
        catalogItems={radiatorCatalogItems}
        loading={radiatorDialog.loading}
        saving={radiatorDialog.saving}
        replacing={radiatorDialog.replacing}
        revisions={radiatorDialog.revisions}
        device={radiatorDialog.device}
        onClose={() =>
          setRadiatorDialog({
            open: false,
            device: null,
            loading: false,
            saving: false,
            replacing: false,
            revisions: [],
            data: emptyRadiator,
          })
        }
        onSave={handleSaveRadiator}
        onReplace={handleReplaceRadiator}
      />

      <ReplaceDeviceDialog
        open={replaceDeviceDialog.open}
        device={replaceDeviceDialog.device}
        onClose={() => setReplaceDeviceDialog({ open: false, device: null })}
        onReplaced={() => {
          setReplaceDeviceDialog({ open: false, device: null });
          handleSearchDevices();
          loadReadiness(latestPeriod?.id || null);
        }}
      />

      <ReportDialog
        open={reportDialog.open}
        loading={reportDialog.loading}
        report={reportDialog.payload}
        downloading={reportDownloading}
        onDownload={handleDownloadReport}
        onDownloadC300={handleDownloadC300}
        onDownloadReceiptsZip={handleDownloadReceiptsZip}
        onDownloadApartmentReceipt={handleDownloadApartmentReceipt}
        onClose={() => setReportDialog({ open: false, loading: false, payload: null })}
      />

      <AttachImportDialog
        open={attachImportDialog.open}
        session={attachImportDialog.session}
        loading={attachImportDialog.loading}
        items={attachImportDialog.items}
        actionId={sessionActionId}
        onClose={() => setAttachImportDialog({ open: false, session: null, loading: false, items: [] })}
        onAttach={handleAttachImport}
      />
    </>
  );
}

function HeatSettingsDialog({ open, onClose, settings, setSettings, loading, onSave }) {
  const handleChange = (field) => (event) => {
    setSettings((prev) => ({ ...prev, [field]: event.target.value }));
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Настройки распределения тепла</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Постоянная часть, %"
            type="number"
            inputProps={{ min: 0, max: 100 }}
            value={settings.heat_constant_share_percent}
            onChange={handleChange('heat_constant_share_percent')}
          />
          <TextField
            label="Переменная часть, %"
            type="number"
            inputProps={{ min: 0, max: 100 }}
            value={settings.heat_variable_share_percent}
            onChange={handleChange('heat_variable_share_percent')}
            helperText="Если указаны обе доли, они должны в сумме давать 100%"
          />
          <TextField
            label="Базовый тариф, ₽/Гкал"
            type="number"
            value={settings.heat_default_tariff}
            onChange={handleChange('heat_default_tariff')}
          />
          <TextField
            label="Минимум показаний, %"
            type="number"
            inputProps={{ min: 0, max: 100, step: '0.0001' }}
            value={settings.heat_min_readings_percent}
            onChange={handleChange('heat_min_readings_percent')}
            helperText="Если покрытие площади показаниями ниже этого порога, расчёт будет заблокирован."
          />
          <Typography variant="subtitle2" sx={{ fontWeight: 700, pt: 0.5 }}>
            Период дома
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Заполняйте только если для дома нужен отдельный период. Пустые поля оставляют
            период по умолчанию. Срок добора задаётся в настройках комплекса.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Первый день периода"
              type="number"
              inputProps={{ min: 1, max: 31 }}
              value={settings.heat_period_start_day}
              onChange={handleChange('heat_period_start_day')}
              helperText="Пусто: настройки комплекса"
              sx={{ flex: 1 }}
            />
            <TextField
              label="Последний день периода"
              type="number"
              inputProps={{ min: 1, max: 31 }}
              value={settings.heat_period_end_day}
              onChange={handleChange('heat_period_end_day')}
              helperText="Пусто: настройки комплекса"
              sx={{ flex: 1 }}
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Отмена
        </Button>
        <Button variant="contained" onClick={onSave} disabled={loading}>
          Сохранить
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function PeriodsList({
  periods,
  loading,
  onEdit,
  onDelete,
  onReport,
  onCreateSession,
  sessionActionId,
  periodActionId,
  onCopyPrevious,
  onClearReadings,
  onToggleLock,
}) {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuPeriod, setMenuPeriod] = useState(null);

  const openMenu = (event, period) => {
    setMenuAnchor(event.currentTarget);
    setMenuPeriod(period);
  };

  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuPeriod(null);
  };

  const runMenuAction = (action) => {
    const period = menuPeriod;
    closeMenu();
    if (period) action(period);
  };

  if (loading) {
    return <Typography color="text.secondary">Загрузка периодов...</Typography>;
  }
  if (!periods.length) {
    return <Typography color="text.secondary">Периоды ещё не добавлены.</Typography>;
  }
  return (
    <Stack spacing={1.5}>
      {periods
        .slice()
        .sort((a, b) => new Date(b.period_end).getTime() - new Date(a.period_end).getTime())
        .map((period) => (
          <Paper
            key={period.id}
            variant="outlined"
            sx={{ p: 1.5, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', justifyContent: 'space-between' }}
          >
            <Stack spacing={0.5}>
              <Typography variant="subtitle2">
                {formatPeriodRange(period.period_start, period.period_end, { dateOnly: true })}
              </Typography>
              <Stack direction="row" spacing={0.75} alignItems="center">
                <Chip
                  size="small"
                  color={period.is_locked ? 'success' : 'default'}
                  variant={period.is_locked ? 'filled' : 'outlined'}
                  label={period.is_locked ? 'Период закрыт' : 'Период открыт'}
                />
                {period.copied_previous_readings_at ? (
                  <Chip size="small" variant="outlined" label="Есть дозаполнение" />
                ) : null}
              </Stack>
              <Typography variant="body2" color="text.secondary">
                Потребление: {period.house_consumption_gcal} Гкал • Тариф:{' '}
                {period.tariff !== null && period.tariff !== undefined ? `${period.tariff} ₽/Гкал` : '—'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Доли: {period.constant_share_percent ?? '—'}% / {period.variable_share_percent ?? '—'}%
              </Typography>
              {period.ignore_reduction_coefficients ? (
                <Typography variant="body2" color="text.secondary">
                  Квартирные коэффициенты отключены для этого периода
                </Typography>
              ) : null}
            </Stack>
            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" justifyContent="flex-end">
              <Button size="small" startIcon={<TimelineIcon />} onClick={() => onReport(period)}>
                Отчёт
              </Button>
              <Button
                size="small"
                variant="outlined"
                disabled={sessionActionId === `create:${period.id}`}
                onClick={() => onCreateSession(period)}
              >
                {'\u0421\u0435\u0441\u0441\u0438\u044f'}
              </Button>
              <Tooltip title="Действия с периодом">
                <IconButton
                  size="small"
                  onClick={(event) => openMenu(event, period)}
                  sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}
                >
                  <MoreHorizRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Paper>
        ))}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        slotProps={{ paper: { sx: { minWidth: 230, border: '1px solid', borderColor: 'divider' } } }}
      >
        <MenuItem
          disabled={menuPeriod?.is_locked || Boolean(periodActionId)}
          onClick={() => runMenuAction(onCopyPrevious)}
        >
          Дозаполнить показания
        </MenuItem>
        <MenuItem
          disabled={menuPeriod?.is_locked || Boolean(periodActionId)}
          onClick={() => runMenuAction(onClearReadings)}
        >
          Очистить импортированные показания
        </MenuItem>
        <Divider />
        <MenuItem
          disabled={Boolean(periodActionId)}
          onClick={() => runMenuAction(onToggleLock)}
        >
          {menuPeriod?.is_locked ? 'Разблокировать период' : 'Заблокировать период'}
        </MenuItem>
        <MenuItem
          disabled={menuPeriod?.is_locked}
          onClick={() => runMenuAction(onEdit)}
        >
          Изменить период
        </MenuItem>
        <MenuItem
          disabled={menuPeriod?.is_locked}
          onClick={() => runMenuAction((period) => onDelete(period.id))}
          sx={{ color: 'error.main' }}
        >
          Удалить период
        </MenuItem>
      </Menu>
    </Stack>
  );
}

function SessionsList({ sessions, periods, loading, sessionActionId, onAttachImport, onCalculate, onClose, onOpenReport }) {
  const periodMap = useMemo(
    () => Object.fromEntries(periods.map((item) => [String(item.id), item])),
    [periods]
  );

  if (loading) {
    return <Typography color="text.secondary">{'\u0417\u0430\u0433\u0440\u0443\u0437\u043a\u0430 \u0441\u0435\u0441\u0441\u0438\u0439...'}</Typography>;
  }
  if (!sessions.length) {
    return <Typography color="text.secondary">{'\u0421\u0435\u0441\u0441\u0438\u0438 \u0435\u0449\u0451 \u043d\u0435 \u0441\u043e\u0437\u0434\u0430\u043d\u044b.'}</Typography>;
  }

  return (
    <Stack spacing={1.5}>
      {sessions.map((session) => {
        const period = periodMap[String(session.heat_period_id)] || null;
        const canCalculate = session.status !== 'CLOSED' && Boolean(session.heat_period_id);
        const canClose = session.status === 'CALCULATED';
        const canOpenReport = Boolean(session.report_created_at) || session.status === 'CALCULATED' || session.status === 'CLOSED';
        return (
          <Paper
            key={session.id}
            variant="outlined"
            sx={{ p: 1.5, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', justifyContent: 'space-between' }}
          >
            <Stack spacing={0.5}>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                <Typography variant="subtitle2">{session.title || '\u0421\u0435\u0441\u0441\u0438\u044f \u0440\u0430\u0441\u0447\u0451\u0442\u0430'}</Typography>
                <Chip size="small" label={session.status} />
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {'\u041f\u0435\u0440\u0438\u043e\u0434:'}{' '}
                {period
                  ? formatPeriodRange(period.period_start, period.period_end, { dateOnly: true })
                  : '\u043d\u0435 \u0432\u044b\u0431\u0440\u0430\u043d'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {'\u0417\u0430\u0433\u0440\u0443\u0437\u043a\u0430:'} {session.import_batch_id ? `#${session.import_batch_id}` : '\u043d\u0435 \u043f\u0440\u0438\u0432\u044f\u0437\u0430\u043d\u0430'}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="outlined"
                disabled={sessionActionId === `attach:${session.id}`}
                onClick={() => onAttachImport(session)}
              >
                {'Привязать импорт'}
              </Button>
              <Button
                size="small"
                startIcon={<TimelineIcon />}
                disabled={!canCalculate || sessionActionId === `calc:${session.id}`}
                onClick={() => onCalculate(session)}
              >
                {'\u0420\u0430\u0441\u0441\u0447\u0438\u0442\u0430\u0442\u044c'}
              </Button>
              <Button
                size="small"
                variant="outlined"
                disabled={!canOpenReport}
                onClick={() => onOpenReport(session)}
              >
                Отчёт
              </Button>
              <Button
                size="small"
                color="success"
                disabled={!canClose || sessionActionId === `close:${session.id}`}
                onClick={() => onClose(session)}
              >
                ???????
              </Button>
            </Stack>
          </Paper>
        );
      })}
    </Stack>
  );
}

function AttachImportDialog({ open, session, loading, items, actionId, onClose, onAttach }) {
  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>{'Привязать импорт к сессии'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {session?.title || 'Сессия расчёта'}
          </Typography>
          {loading ? (
            <Typography color="text.secondary">{'Загрузка импортов...'}</Typography>
          ) : null}
          {!loading && !items.length ? (
            <Typography color="text.secondary">{'Нет доступных импортов показаний.'}</Typography>
          ) : null}
          {!loading &&
            items.map((item) => (
              <Paper
                key={item.id}
                variant="outlined"
                sx={{ p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}
              >
                <Stack spacing={0.5}>
                  <Typography variant="subtitle2">{item.file_name || 'Импорт показаний'}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {`Строк: ${item.total_rows ?? '—'} • Успешно: ${item.imported_rows ?? '—'} • Пропущено: ${item.skipped_rows ?? '—'}`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {item.created_at ? new Date(item.created_at).toLocaleString('ru-RU') : '—'}
                  </Typography>
                </Stack>
                <Button
                  variant="contained"
                  disabled={actionId === `attach:${session?.id}`}
                  onClick={() => onAttach(item)}
                >
                  {'Привязать'}
                </Button>
              </Paper>
            ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading || actionId === `attach:${session?.id}`}>
          {'Закрыть'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function RadiatorCatalogDialog({
  open,
  items,
  form,
  setForm,
  loading,
  saving,
  onClose,
  onSave,
  onEdit,
  onDelete,
}) {
  const handleChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="lg">
      <DialogTitle>Подбор радиаторов УРТ</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="body2" color="text.secondary">
              Записи справочника привязаны к текущему tenant и помогают при подборе коэффициентов, но заполнение вручную тоже поддерживается.
            </Typography>
            <Button size="small" variant="outlined" onClick={() => setForm(emptyCatalogForm)}>
              Новая запись
            </Button>
          </Stack>

          {loading ? (
            <Typography color="text.secondary">Загрузка справочника...</Typography>
          ) : (
            <Stack spacing={1}>
              {items.map((item) => (
                <Paper
                  key={item.id}
                  variant="outlined"
                  sx={{ p: 1.25, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}
                >
                  <Stack spacing={0.35}>
                    <Typography variant="subtitle2">{[item.manufacturer, item.name].filter(Boolean).join(' • ')}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {[
                        item.model_code,
                        item.radiator_type,
                        item.heat_coefficient != null ? `K=${item.heat_coefficient}` : null,
                      ].filter(Boolean).join(' • ')}
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1}>
                    <Button size="small" onClick={() => onEdit(item)}>Изменить</Button>
                    <Button size="small" color="error" onClick={() => onDelete(item)}>Удалить</Button>
                  </Stack>
                </Paper>
              ))}
              {!items.length && (
                <Typography color="text.secondary">Справочник пока пуст.</Typography>
              )}
            </Stack>
          )}

          <Divider />

          <Stack spacing={2}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {form.id ? 'Редактирование записи' : 'Новая запись'}
            </Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField label="Название" value={form.name} onChange={handleChange('name')} sx={{ flex: 1 }} />
              <TextField label="Производитель" value={form.manufacturer} onChange={handleChange('manufacturer')} sx={{ flex: 1 }} />
            </Stack>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField label="Код модели" value={form.model_code} onChange={handleChange('model_code')} sx={{ flex: 1 }} />
              <TextField label="Тип радиатора" value={form.radiator_type} onChange={handleChange('radiator_type')} sx={{ flex: 1 }} />
              <TextField label="Расположение" value={form.placement} onChange={handleChange('placement')} sx={{ flex: 1 }} />
            </Stack>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField label="Секции" value={form.section_count} onChange={handleChange('section_count')} sx={{ flex: 1 }} />
              <TextField label="Мощность, Вт" type="number" value={form.power_watts} onChange={handleChange('power_watts')} sx={{ flex: 1 }} />
              <TextField label="Коэффициент" type="number" value={form.heat_coefficient} onChange={handleChange('heat_coefficient')} sx={{ flex: 1 }} />
            </Stack>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField label="Высота, мм" type="number" value={form.height_mm} onChange={handleChange('height_mm')} sx={{ flex: 1 }} />
              <TextField label="Ширина, мм" type="number" value={form.width_mm} onChange={handleChange('width_mm')} sx={{ flex: 1 }} />
              <TextField label="Глубина, мм" type="number" value={form.depth_mm} onChange={handleChange('depth_mm')} sx={{ flex: 1 }} />
            </Stack>
            <FormControlLabel
              control={<Switch checked={form.is_corner} onChange={handleChange('is_corner')} />}
              label="Угловой радиатор"
            />
            <FormControlLabel
              control={<Switch checked={form.is_active} onChange={handleChange('is_active')} />}
              label="Активная запись"
            />
            <TextField label="Заметки" multiline minRows={2} value={form.notes} onChange={handleChange('notes')} />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Закрыть</Button>
        <Button variant="contained" onClick={onSave} disabled={saving || loading}>Сохранить</Button>
      </DialogActions>
    </Dialog>
  );
}

function HeatReadinessCard({ readiness, loading, allowMissingCoefficientsArea = false }) {
  if (loading) {
    return (
      <Paper
        variant="outlined"
        sx={{
          p: 1.5,
          borderRadius: 2.5,
          backgroundColor: 'rgba(248, 250, 252, 0.72)',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Проверяем готовность расчёта УРТ...
        </Typography>
      </Paper>
    );
  }

  if (!readiness) {
    return null;
  }

  const { summary, issues } = readiness;
  const missingCoefficientCount = Number(summary.missing_coefficient_count || 0);
  const totalErrorsCount = Number(summary.errors_count || 0);
  const blockingErrorsCount = allowMissingCoefficientsArea
    ? Math.max(totalErrorsCount - missingCoefficientCount, 0)
    : totalErrorsCount;
  const warningCount = Number(summary.warnings_count || 0) + (allowMissingCoefficientsArea ? missingCoefficientCount : 0);
  const calculationReadyDevices = allowMissingCoefficientsArea
    ? Math.min(Number(summary.devices_total || 0), Number(summary.devices_ready || 0) + missingCoefficientCount)
    : Number(summary.devices_ready || 0);
  const groupedIssues = issues.reduce((acc, issue) => {
    const issueKey = [
      issue.code,
      issue.apartment_number || issue.apartment_id || '',
      issue.device_name || '',
      issue.message || '',
      allowMissingCoefficientsArea && issue.code === 'coefficient_missing' ? 'fallback' : issue.severity || '',
    ].join('|');
    const existing = acc.get(issueKey);

    if (existing) {
      existing.count += 1;
      return acc;
    }

    acc.set(issueKey, { ...issue, count: 1 });
    return acc;
  }, new Map());
  const normalizedIssues = Array.from(groupedIssues.values()).map((issue) => ({
    ...issue,
    severity: allowMissingCoefficientsArea && issue.code === 'coefficient_missing' ? 'warning' : issue.severity,
  }));
  const visibleIssues = normalizedIssues.slice(0, 6);
  const onlyCoefficientErrors =
    allowMissingCoefficientsArea &&
    totalErrorsCount > 0 &&
    blockingErrorsCount === 0;
  const canCalculate = summary.is_ready || onlyCoefficientErrors;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        borderRadius: 2.5,
        borderColor: canCalculate ? 'rgba(22, 163, 74, 0.24)' : 'rgba(239, 68, 68, 0.22)',
        backgroundColor: canCalculate ? 'rgba(240, 253, 244, 0.62)' : 'rgba(255, 251, 235, 0.8)',
      }}
    >
      <Stack spacing={1.25}>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Готовность расчёта
          </Typography>
          <Chip
            size="small"
            color={canCalculate ? 'success' : 'warning'}
            variant={canCalculate ? 'filled' : 'outlined'}
            label={canCalculate ? 'Можно рассчитывать' : 'Нужна проверка'}
          />
          <Chip
            size="small"
            variant="outlined"
            color={blockingErrorsCount > 0 ? 'error' : 'default'}
            label={
              allowMissingCoefficientsArea
                ? `Блокирующие: ${blockingErrorsCount}`
                : `Ошибки: ${totalErrorsCount}`
            }
          />
          <Chip size="small" variant="outlined" label={`Предупреждения: ${warningCount}`} />
          <Chip
            size="small"
            variant="outlined"
            label={
              allowMissingCoefficientsArea
                ? `К расчёту: ${calculationReadyDevices}/${summary.devices_total}`
                : `УРТ: ${summary.devices_ready}/${summary.devices_total}`
            }
          />
          <Chip
            size="small"
            variant="outlined"
            label={`Покрытие: ${Number(summary.readings_coverage_percent || 0).toFixed(2)}% / ${Number(
              summary.minimum_required_coverage_percent || 0
            ).toFixed(2)}%`}
          />
        </Stack>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {summary.invalid_settings_count > 0 && (
            <Chip size="small" color="error" variant="outlined" label={`Настройки: ${summary.invalid_settings_count}`} />
          )}
          {summary.missing_area_count > 0 && (
            <Chip size="small" color="error" variant="outlined" label={`Площадь: ${summary.missing_area_count}`} />
          )}
          {missingCoefficientCount > 0 && (
            <Chip
              size="small"
              color={allowMissingCoefficientsArea ? 'warning' : 'error'}
              variant="outlined"
              label={
                allowMissingCoefficientsArea
                  ? `По площади: ${missingCoefficientCount}`
                  : `Коэффициенты: ${missingCoefficientCount}`
              }
            />
          )}
          {summary.missing_readings_count > 0 && (
            <Chip size="small" color="error" variant="outlined" label={`Показания: ${summary.missing_readings_count}`} />
          )}
          {summary.direct_devices_count > 0 && (
            <Chip size="small" variant="outlined" label={`УПД: ${summary.direct_devices_count}`} />
          )}
          {summary.allocator_devices_count > 0 && (
            <Chip size="small" variant="outlined" label={`УРТ: ${summary.allocator_devices_count}`} />
          )}
        </Stack>

        {visibleIssues.length > 0 && (
          <Stack spacing={0.75}>
            {visibleIssues.map((issue, index) => (
              <Alert
                key={`${issue.code}-${issue.device_id || issue.apartment_id || index}`}
                severity={issue.severity === 'error' ? 'error' : 'warning'}
                sx={{ py: 0.25, alignItems: 'center' }}
              >
                {issue.message}
                {issue.apartment_number ? ` Квартира: ${issue.apartment_number}.` : ''}
                {issue.device_name ? ` Устройство: ${issue.device_name}.` : ''}
                {issue.count > 1 ? ` Количество: ${issue.count}.` : ''}
              </Alert>
            ))}
            {normalizedIssues.length > visibleIssues.length && (
              <Typography variant="caption" color="text.secondary">
                И ещё проблем: {normalizedIssues.length - visibleIssues.length}
              </Typography>
            )}
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}

function PeriodDialog({
  open,
  data,
  setData,
  loading,
  onClose,
  onSave,
  isEdit,
  periodStartDay = '',
  periodEndDay = '',
  minDate,
}) {
  const handleChange = (field) => (event) => {
    setData({ ...data, [field]: event.target.value });
  };
  const handleReportMonthChange = (event) => {
    const reportMonth = event.target.value;
    setData({
      ...data,
      report_month: reportMonth,
      ...buildPeriodWindow(reportMonth, periodStartDay, periodEndDay),
    });
  };
  const periodRuleHint = formatPeriodRuleLabel(periodStartDay, periodEndDay);

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isEdit ? 'Изменение периода' : 'Новый период'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {!isEdit && (
            <TextField
              label="Отчётный месяц"
              type="month"
              inputProps={{ min: minDate ? String(minDate).slice(0, 7) : undefined }}
              value={data.report_month}
              onChange={handleReportMonthChange}
              helperText={`Даты подставятся по настройке дома: ${periodRuleHint.replace('Правило периода: ', '')}`}
              InputLabelProps={{ shrink: true }}
            />
          )}
          <TextField
            label="Начало периода"
            type="date"
            inputProps={{ min: minDate || undefined }}
            value={data.period_start}
            onChange={handleChange('period_start')}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Окончание периода"
            type="date"
            inputProps={{ min: minDate || undefined }}
            value={data.period_end}
            onChange={handleChange('period_end')}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Показание ОДПУ, Гкал"
            type="number"
            value={data.house_consumption_gcal}
            onChange={handleChange('house_consumption_gcal')}
          />
          <TextField
            label="Тариф, ₽/Гкал"
            type="number"
            value={data.tariff}
            onChange={handleChange('tariff')}
          />
          <Stack direction="row" spacing={2}>
            <TextField
              label="Постоянная, %"
              type="number"
              value={data.constant_share_percent}
              onChange={handleChange('constant_share_percent')}
              sx={{ flex: 1 }}
            />
            <TextField
              label="Переменная, %"
              type="number"
              value={data.variable_share_percent}
              onChange={handleChange('variable_share_percent')}
              sx={{ flex: 1 }}
            />
          </Stack>
          <FormControlLabel
            control={
              <Switch
                checked={!!data.ignore_reduction_coefficients}
                onChange={(event) =>
                  setData({ ...data, ignore_reduction_coefficients: event.target.checked })
                }
              />
            }
            label="Не учитывать квартирные коэффициенты"
          />
          <TextField
            label="Заметки"
            multiline
            minRows={2}
            value={data.notes}
            onChange={handleChange('notes')}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Отмена
        </Button>
        <Button variant="contained" onClick={onSave} disabled={loading}>
          Сохранить
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function DeviceResults({ results, loading, onEdit, onReplace, onOpenApartment }) {
  if (loading) {
    return <Typography color="text.secondary">Выполняется поиск устройств...</Typography>;
  }
  if (!results.length) {
    return (
      <Typography color="text.secondary">
        В списке отображаются только устройства типа «УРТ». Используйте поиск по адресу или серийному номеру.
      </Typography>
    );
  }
  return (
    <Stack spacing={1}>
      {results.map((device) => (
        <Paper
          key={device.id}
          variant="outlined"
          sx={{ p: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        >
          <div>
            <Typography variant="subtitle2">{device.serial_number || device.name || 'Без имени'}</Typography>
            <Typography variant="body2" color="text.secondary">
              {device.building_name || '—'} • {device.corp_name || '—'} • кв. {device.apartment_number || '—'}
            </Typography>
          </div>
          <Stack direction="row" spacing={1}>
            {device.apartment_id ? (
              <Button size="small" variant="text" onClick={() => onOpenApartment?.(device)}>
                Квартира
              </Button>
            ) : null}
            <Button size="small" variant="outlined" onClick={() => onEdit(device)}>
              Паспорт
            </Button>
            <Button size="small" variant="outlined" color="warning" onClick={() => onReplace(device)}>
              Заменить
            </Button>
          </Stack>
        </Paper>
      ))}
    </Stack>
  );
}

function RadiatorDialog({
  open,
  data,
  setData,
  catalogItems = [],
  loading,
  saving,
  replacing = false,
  revisions = [],
  onSave,
  onReplace,
  onClose,
  device,
}) {
  const handleChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setData((prev) => ({ ...prev, [field]: value }));
  };

  const selectedCatalogItem =
    catalogItems.find((item) => String(item.id) === String(data.catalog_id || '')) || null;

  const handleCatalogChange = (_, item) => {
    if (!item) {
      setData((prev) => ({ ...prev, catalog_id: null }));
      return;
    }
    setData((prev) => ({
      ...prev,
      catalog_id: item.id,
      model_name: item.name || prev.model_name,
      manufacturer: item.manufacturer || prev.manufacturer,
      placement: item.placement || prev.placement,
      section_count: item.section_count ?? prev.section_count,
      power_watts:
        item.power_watts !== null && item.power_watts !== undefined ? String(item.power_watts) : prev.power_watts,
      height_mm:
        item.height_mm !== null && item.height_mm !== undefined ? String(item.height_mm) : prev.height_mm,
      width_mm:
        item.width_mm !== null && item.width_mm !== undefined ? String(item.width_mm) : prev.width_mm,
      depth_mm:
        item.depth_mm !== null && item.depth_mm !== undefined ? String(item.depth_mm) : prev.depth_mm,
      is_corner: typeof item.is_corner === 'boolean' ? item.is_corner : prev.is_corner,
      heat_coefficient:
        item.heat_coefficient !== null && item.heat_coefficient !== undefined
          ? String(item.heat_coefficient)
          : prev.heat_coefficient,
      heat_reduction_coefficient:
        item.heat_reduction_coefficient !== null && item.heat_reduction_coefficient !== undefined
          ? String(item.heat_reduction_coefficient)
          : prev.heat_reduction_coefficient,
    }));
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Паспорт радиатора {device?.serial_number ? `(${device.serial_number})` : ''}</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Typography color="text.secondary">Загрузка данных...</Typography>
        ) : (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Autocomplete
              options={catalogItems}
              value={selectedCatalogItem}
              onChange={handleCatalogChange}
              getOptionLabel={(option) => [option.manufacturer, option.name, option.model_code].filter(Boolean).join(' • ')}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Модель радиатора"
                  helperText="Можно выбрать из справочника или заполнить вручную."
                />
              )}
            />
            <TextField label="Модель" value={data.model_name} onChange={handleChange('model_name')} />
            <TextField label="Производитель" value={data.manufacturer} onChange={handleChange('manufacturer')} />
            <Stack direction="row" spacing={2}>
              <TextField
                label="Расположение (комната)"
                value={data.placement}
                onChange={handleChange('placement')}
                sx={{ flex: 1 }}
              />
              <TextField
                label="Метка помещения"
                value={data.room_label}
                onChange={handleChange('room_label')}
                sx={{ flex: 1 }}
              />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Количество секций"
                type="number"
                value={data.section_count}
                onChange={handleChange('section_count')}
                sx={{ flex: 1 }}
              />
              <TextField
                label="Мощность, Вт"
                type="number"
                value={data.power_watts}
                onChange={handleChange('power_watts')}
                sx={{ flex: 1 }}
              />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField label="Высота, мм" type="number" value={data.height_mm} onChange={handleChange('height_mm')} />
              <TextField label="Ширина, мм" type="number" value={data.width_mm} onChange={handleChange('width_mm')} />
              <TextField label="Глубина, мм" type="number" value={data.depth_mm} onChange={handleChange('depth_mm')} />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Расчётный коэффициент"
                type="number"
                value={data.heat_coefficient}
                onChange={handleChange('heat_coefficient')}
                helperText="Используется в расчёте отопления"
                sx={{ flex: 1 }}
              />
              <TextField
                label="Понижающий коэффициент"
                type="number"
                value={data.heat_reduction_coefficient}
                onChange={handleChange('heat_reduction_coefficient')}
                helperText="Только если коэффициент задан на приборе"
                sx={{ flex: 1 }}
              />
            </Stack>
            <FormControlLabel
              control={<Switch checked={data.is_corner} onChange={handleChange('is_corner')} />}
              label="Угловой радиатор"
            />
            <Divider />
            <Typography variant="subtitle2">Замена радиатора</Typography>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Дата замены"
                type="date"
                value={data.valid_from || ''}
                onChange={handleChange('valid_from')}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1 }}
              />
              <TextField
                label="Причина"
                value={data.reason || ''}
                onChange={handleChange('reason')}
                sx={{ flex: 1 }}
              />
            </Stack>
            {revisions.length > 0 && (
              <Paper variant="outlined" sx={{ p: 1, maxHeight: 130, overflow: 'auto' }}>
                <Stack spacing={0.75}>
                  {revisions.map((item) => (
                    <Stack key={item.id} direction="row" justifyContent="space-between" gap={1}>
                      <Typography variant="body2">
                        {item.valid_from || '—'} - {item.valid_to || 'сейчас'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {item.model_name || item.reason || 'Запись'}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            )}
            <TextField
              label="Заметки"
              multiline
              minRows={2}
              value={data.notes}
              onChange={handleChange('notes')}
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Отмена
        </Button>
        <Button variant="contained" onClick={onSave} disabled={saving || loading}>
          Сохранить
        </Button>
        <Button variant="outlined" color="warning" onClick={onReplace} disabled={saving || replacing || loading}>
          Зафиксировать замену
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ReportDialog({
  open,
  loading,
  report,
  downloading = false,
  onDownload,
  onDownloadC300,
  onDownloadReceiptsZip,
  onDownloadApartmentReceipt,
  onClose,
}) {
  const navigate = useNavigate();
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState(null);

  useEffect(() => {
    if (!open) setDownloadMenuAnchor(null);
  }, [open]);

  const closeDownloadMenu = () => setDownloadMenuAnchor(null);
  const selectDownloadFormat = (format) => {
    closeDownloadMenu();
    if (format === 'c300') {
      onDownloadC300?.();
      return;
    }
    onDownload?.(format);
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="lg">
      <DialogTitle>Отчёт по распределению тепла</DialogTitle>
      <DialogContent dividers>
        {loading || !report ? (
          <Typography color="text.secondary">Формирование отчёта...</Typography>
        ) : (
          <Stack spacing={2}>
            <Stack direction="row" spacing={2} flexWrap="wrap">
              <Chip label={`Здание: ${report.summary.building_name ?? report.summary.building_id}`} />
              <Chip label={`Период: ${report.summary.period_start} — ${report.summary.period_end}`} />
              <Chip label={`ОДПУ: ${report.summary.total_house_consumption} Гкал`} />
              <Chip label={`Оплачено: ${report.summary.total_payments.toFixed(2)} ₽`} />
            </Stack>
            <Typography variant="subtitle2">Квартиры</Typography>
            <Paper variant="outlined" sx={{ maxHeight: 360, overflow: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Квартира</TableCell>
                    <TableCell>Лицевой счёт</TableCell>
                    <TableCell>Площадь</TableCell>
                    <TableCell>Данные</TableCell>
                    <TableCell>Основание</TableCell>
                    <TableCell>Гкал</TableCell>
                    <TableCell>Платёж, ₽</TableCell>
                    <TableCell align="right">Квитанция</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.apartments.map((row) => (
                    <TableRow key={row.apartment_id} hover>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ color: '#2368B5', fontWeight: 800, cursor: row.apartment_id ? 'pointer' : 'default' }}
                          onClick={() => row.apartment_id && navigate(`/apartments/${row.apartment_id}`)}
                        >
                          {row.apartment_number || row.apartment_id}
                        </Typography>
                      </TableCell>
                      <TableCell>{row.account_number || '—'}</TableCell>
                      <TableCell>{row.area ?? '—'}</TableCell>
                      <TableCell>{row.has_data ? 'Есть' : 'Нет'}</TableCell>
                      <TableCell>{row.calculation_reason || row.calculation_basis || '—'}</TableCell>
                      <TableCell>{row.total_gcal ?? '—'}</TableCell>
                      <TableCell>
                        {row.total_payment !== null && row.total_payment !== undefined
                          ? row.total_payment.toFixed(2)
                          : '—'}
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          startIcon={<FileDownloadOutlinedIcon />}
                          disabled={downloading || !row.apartment_id}
                          onClick={() => onDownloadApartmentReceipt?.(row)}
                        >
                          Excel+PDF
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {report && !loading && (
          <>
            <Button
              startIcon={<FileDownloadOutlinedIcon />}
              onClick={(event) => setDownloadMenuAnchor(event.currentTarget)}
              disabled={downloading}
              aria-haspopup="menu"
              aria-expanded={Boolean(downloadMenuAnchor)}
            >
              Скачать отчёт
            </Button>
            <Menu
              anchorEl={downloadMenuAnchor}
              open={Boolean(downloadMenuAnchor)}
              onClose={closeDownloadMenu}
            >
              <MenuItem onClick={() => selectDownloadFormat('short')}>
                Короткий Excel
              </MenuItem>
              <MenuItem onClick={() => selectDownloadFormat('full')}>
                Полный Excel
              </MenuItem>
              <MenuItem onClick={() => selectDownloadFormat('c300')}>
                CSV для С-300
              </MenuItem>
            </Menu>
            <Button
              startIcon={<FileDownloadOutlinedIcon />}
              onClick={() => onDownloadReceiptsZip?.()}
              disabled={downloading}
            >
              Скачать квитанции
            </Button>
          </>
        )}
        <Button onClick={onClose}>Закрыть</Button>
      </DialogActions>
    </Dialog>
  );
}
